import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiFetch } from "../../services/api";
import { exportDataQualityToExcel } from "../../utils/reportExport";
import ScopeFiltersPanel from "./ScopeFiltersPanel";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole();
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

const DataQualityReportViewer = ({ reportId, report, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [sortColumn, setSortColumn] = useState("quality_score");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterText, setFilterText] = useState("");
  const [showOnlyIssues, setShowOnlyIssues] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  // Scope filters state
  const [scopeFilters, setScopeFilters] = useState({
    filterOrganizations: [],
    filterInitiatives: [],
    filterCapabilities: [],
    filterContexts: [],
  });
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [savingFilters, setSavingFilters] = useState(false);

  const reportConfig = report?.configuration || {};

  // Handle Excel export
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      exportDataQualityToExcel(report, data);
    } catch (err) {
      debugError("Export error:", err);
      setError("Failed to export report: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Load saved filters from report configuration
  useEffect(() => {
    if (reportConfig.scopeFilters) {
      setScopeFilters(reportConfig.scopeFilters);
    }
  }, [report?.id]);

  useEffect(() => {
    executeReport();
  }, [reportId, report?.id]);

  // Re-execute report when scope filters change
  const executeReportWithFilters = useCallback(
    async (filters) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFetch(
          `/api/reports/${reportId || report.id}/execute`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              scope_filters: filters,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to execute report");
        }

        const result = await response.json();
        if (result.success && result.result) {
          setData(result.result);
          // Expand all groups by default
          if (result.result.data?.grouped) {
            setExpandedGroups(new Set(Object.keys(result.result.data.grouped)));
          }
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (err) {
        debugError("Error executing report:", err);
        setError(err.message || "Failed to execute report");
      } finally {
        setLoading(false);
      }
    },
    [reportId, report?.id]
  );

  const executeReport = async () => {
    await executeReportWithFilters(scopeFilters);
  };

  // Handle scope filter changes
  const handleScopeFiltersChange = useCallback(
    (newFilters) => {
      setScopeFilters(newFilters);
      setFiltersDirty(true);
      // Re-execute report with new filters
      executeReportWithFilters(newFilters);
    },
    [executeReportWithFilters]
  );

  // Save filters to report configuration
  const saveFilters = async () => {
    if (!report?.id) return;

    setSavingFilters(true);
    try {
      const updatedConfig = {
        ...reportConfig,
        scopeFilters: scopeFilters,
      };

      const response = await apiFetch(`/api/reports/${report.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          configuration: updatedConfig,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save filters");
      }

      setFiltersDirty(false);
    } catch (err) {
      debugError("Error saving filters:", err);
      setError("Failed to save filters: " + err.message);
    } finally {
      setSavingFilters(false);
    }
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    if (!data?.data?.rows) return [];

    let rows = [...data.data.rows];

    // Filter by text
    if (filterText) {
      const lowerFilter = filterText.toLowerCase();
      rows = rows.filter(
        (row) =>
          row.name?.toLowerCase().includes(lowerFilter) ||
          Object.values(row.attributes || {}).some((v) =>
            String(v).toLowerCase().includes(lowerFilter)
          ) ||
          Object.values(row.custom_attributes || {}).some((v) =>
            String(v).toLowerCase().includes(lowerFilter)
          )
      );
    }

    // Filter by issues
    if (showOnlyIssues) {
      rows = rows.filter((row) => row.issues && row.issues.length > 0);
    }

    // Sort
    rows.sort((a, b) => {
      let aVal, bVal;

      if (sortColumn === "name") {
        aVal = a.name || "";
        bVal = b.name || "";
      } else if (sortColumn === "quality_score") {
        aVal = a.quality_score || 0;
        bVal = b.quality_score || 0;
      } else if (sortColumn === "issues") {
        aVal = a.issues?.length || 0;
        bVal = b.issues?.length || 0;
      } else if (
        data.columns?.find((c) => c.name === sortColumn)?.type === "custom"
      ) {
        aVal = a.custom_attributes?.[sortColumn] || "";
        bVal = b.custom_attributes?.[sortColumn] || "";
      } else {
        aVal = a.attributes?.[sortColumn] || "";
        bVal = b.attributes?.[sortColumn] || "";
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortDirection === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return rows;
  }, [data, filterText, showOnlyIssues, sortColumn, sortDirection]);

  // Group processed data
  const groupedData = useMemo(() => {
    if (!data?.grouping_enabled) {
      return { "All Entities": processedData };
    }

    const groups = {};
    processedData.forEach((row) => {
      const groupKey = row.group || "Unassigned";
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(row);
    });
    return groups;
  }, [data, processedData]);

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getQualityScoreColor = (score) => {
    if (score >= 90) return "text-green-400";
    if (score >= 70) return "text-yellow-400";
    if (score >= 50) return "text-orange-400";
    return "text-red-400";
  };

  const getQualityScoreBg = (score) => {
    if (score >= 90) return "bg-green-900/30";
    if (score >= 70) return "bg-yellow-900/30";
    if (score >= 50) return "bg-orange-900/30";
    return "bg-red-900/30";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-8">
          <div className="flex flex-col items-center">
            <svg
              className="animate-spin w-12 h-12 text-blue-500 mb-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-gray-300 text-lg">Loading report...</p>
            <p className="text-gray-500 text-sm mt-2">
              Analyzing data quality...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-6">
            <h3 className="text-red-200 font-semibold mb-2">
              Error Loading Report
            </h3>
            <p className="text-red-300">{error}</p>
            <div className="mt-4 flex gap-3">
              <button
                onClick={executeReport}
                className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Rerun
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Back to Reports
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const columns = data?.columns || [];

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white mb-2 flex items-center gap-1 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Reports
            </button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              {report?.name || "Data Quality Report"}
              {filtersDirty && (
                <span className="text-xs bg-yellow-600 text-yellow-100 px-2 py-0.5 rounded-full font-normal">
                  Unsaved filters
                </span>
              )}
            </h1>
            <p className="text-gray-400 mt-1">
              {data?.entity_type && `Entity Type: ${data.entity_type}`}
              {data?.generated_at &&
                ` • Generated: ${new Date(data.generated_at).toLocaleString()}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Scope Filters */}
            <ScopeFiltersPanel
              scopeFilters={scopeFilters}
              onFiltersChange={handleScopeFiltersChange}
              onSaveFilters={saveFilters}
              filtersDirty={filtersDirty}
              savingFilters={savingFilters}
            />
            {/* Export to Excel Button */}
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors"
            >
              {exporting ? (
                <svg
                  className="animate-spin w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              )}
              Export to Excel
            </button>
            <button
              onClick={executeReport}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Total Entities</div>
            <div className="text-2xl font-bold text-white">
              {summary.total_entities || 0}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Complete</div>
            <div className="text-2xl font-bold text-green-400">
              {summary.complete_entities || 0}
            </div>
            <div className="text-xs text-gray-500">
              {summary.total_entities > 0
                ? `${Math.round((summary.complete_entities / summary.total_entities) * 100)}%`
                : "0%"}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Incomplete</div>
            <div className="text-2xl font-bold text-yellow-400">
              {summary.incomplete_entities || 0}
            </div>
            <div className="text-xs text-gray-500">
              {summary.total_entities > 0
                ? `${Math.round((summary.incomplete_entities / summary.total_entities) * 100)}%`
                : "0%"}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="text-gray-400 text-sm mb-1">Issues Found</div>
            <div className="text-2xl font-bold text-red-400">
              {(summary.issues_by_type?.missing_required || 0) +
                (summary.issues_by_type?.missing_optional || 0)}
            </div>
            <div className="text-xs text-gray-500">
              {summary.issues_by_type?.missing_required || 0} required
            </div>
          </div>
        </div>

        {/* Attribute Completeness */}
        {Object.keys(summary.attribute_completeness || {}).length > 0 && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              Attribute Completeness
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(summary.attribute_completeness || {}).map(
                ([attr, stats]) => (
                  <div key={attr} className="bg-gray-900 rounded-lg p-3">
                    <div
                      className="text-sm text-gray-400 truncate"
                      title={attr}
                    >
                      {attr
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </div>
                    <div
                      className={`text-xl font-bold ${getQualityScoreColor(stats.percentage)}`}
                    >
                      {stats.percentage}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {stats.filled} / {stats.filled + stats.empty}
                    </div>
                    <div className="mt-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${stats.percentage >= 90 ? "bg-green-500" : stats.percentage >= 70 ? "bg-yellow-500" : stats.percentage >= 50 ? "bg-orange-500" : "bg-red-500"}`}
                        style={{ width: `${stats.percentage}%` }}
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search entities..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            <label className="flex items-center gap-2 text-gray-300">
              <input
                type="checkbox"
                checked={showOnlyIssues}
                onChange={(e) => setShowOnlyIssues(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500"
              />
              Show only entities with issues
            </label>
            <div className="text-gray-400 text-sm">
              Showing {processedData.length} of {summary.total_entities || 0}{" "}
              entities
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700 bg-gray-900">
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer hover:text-white"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortColumn === "name" && (
                        <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col.name}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer hover:text-white"
                      onClick={() => handleSort(col.name)}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {col.type === "custom" && (
                          <span className="text-blue-400 text-xs">●</span>
                        )}
                        {sortColumn === col.name && (
                          <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer hover:text-white"
                    onClick={() => handleSort("quality_score")}
                  >
                    <div className="flex items-center gap-1">
                      Quality Score
                      {sortColumn === "quality_score" && (
                        <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer hover:text-white"
                    onClick={() => handleSort("issues")}
                  >
                    <div className="flex items-center gap-1">
                      Issues
                      {sortColumn === "issues" && (
                        <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {data?.grouping_enabled
                  ? // Grouped view
                    Object.entries(groupedData).map(([groupName, rows]) => (
                      <React.Fragment key={groupName}>
                        <tr
                          className="bg-gray-700/50 cursor-pointer hover:bg-gray-700"
                          onClick={() => toggleGroup(groupName)}
                        >
                          <td
                            colSpan={columns.length + 3}
                            className="px-4 py-2"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">
                                {expandedGroups.has(groupName) ? "▼" : "▶"}
                              </span>
                              <span className="font-medium text-white">
                                {groupName}
                              </span>
                              <span className="text-gray-400 text-sm">
                                ({rows.length} entities)
                              </span>
                            </div>
                          </td>
                        </tr>
                        {expandedGroups.has(groupName) &&
                          rows.map((row) => (
                            <tr
                              key={row.id}
                              className={`hover:bg-gray-700/50 ${getQualityScoreBg(row.quality_score)}`}
                            >
                              <td className="px-4 py-3 text-white font-medium pl-8">
                                {row.name}
                              </td>
                              {columns.map((col) => (
                                <td
                                  key={col.name}
                                  className="px-4 py-3 text-gray-300"
                                >
                                  {col.type === "custom"
                                    ? row.custom_attributes?.[col.name] || (
                                        <span className="text-gray-500">—</span>
                                      )
                                    : row.attributes?.[col.name] || (
                                        <span className="text-gray-500">—</span>
                                      )}
                                </td>
                              ))}
                              <td className="px-4 py-3">
                                <span
                                  className={`font-bold ${getQualityScoreColor(row.quality_score)}`}
                                >
                                  {row.quality_score}%
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {row.issues && row.issues.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {row.issues
                                      .slice(0, 2)
                                      .map((issue, idx) => (
                                        <span
                                          key={idx}
                                          className={`text-xs px-2 py-1 rounded ${
                                            issue.type === "missing_required"
                                              ? "bg-red-900/50 text-red-300"
                                              : "bg-yellow-900/50 text-yellow-300"
                                          }`}
                                          title={issue.message}
                                        >
                                          {issue.attribute}
                                        </span>
                                      ))}
                                    {row.issues.length > 2 && (
                                      <span className="text-xs text-gray-400">
                                        +{row.issues.length - 2}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-green-400">✓</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))
                  : // Flat view
                    processedData.map((row) => (
                      <tr
                        key={row.id}
                        className={`hover:bg-gray-700/50 ${getQualityScoreBg(row.quality_score)}`}
                      >
                        <td className="px-4 py-3 text-white font-medium">
                          {row.name}
                        </td>
                        {columns.map((col) => (
                          <td
                            key={col.name}
                            className="px-4 py-3 text-gray-300"
                          >
                            {col.type === "custom"
                              ? row.custom_attributes?.[col.name] || (
                                  <span className="text-gray-500">—</span>
                                )
                              : row.attributes?.[col.name] || (
                                  <span className="text-gray-500">—</span>
                                )}
                          </td>
                        ))}
                        <td className="px-4 py-3">
                          <span
                            className={`font-bold ${getQualityScoreColor(row.quality_score)}`}
                          >
                            {row.quality_score}%
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {row.issues && row.issues.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {row.issues.slice(0, 2).map((issue, idx) => (
                                <span
                                  key={idx}
                                  className={`text-xs px-2 py-1 rounded ${
                                    issue.type === "missing_required"
                                      ? "bg-red-900/50 text-red-300"
                                      : "bg-yellow-900/50 text-yellow-300"
                                  }`}
                                  title={issue.message}
                                >
                                  {issue.attribute}
                                </span>
                              ))}
                              {row.issues.length > 2 && (
                                <span className="text-xs text-gray-400">
                                  +{row.issues.length - 2}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-green-400">✓</span>
                          )}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          {processedData.length === 0 && (
            <div className="p-8 text-center text-gray-400">
              {filterText || showOnlyIssues
                ? "No entities match your filters"
                : "No entities found for this report"}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <span className="text-blue-400">●</span>
            <span>Custom Attribute</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-green-500"></span>
            <span>90%+ Complete</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-yellow-500"></span>
            <span>70-89% Complete</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-orange-500"></span>
            <span>50-69% Complete</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-red-500"></span>
            <span>&lt;50% Complete</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataQualityReportViewer;
